import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { SiteHeader } from '../components/SiteLayout';
import { StatusBadge, ModeBadgeSmall } from '../components/AssessmentBadges';
import StructureLookupWidget from '../components/StructureLookupWidget';
import KnowledgeBasePanel from '../components/KnowledgeBasePanel';
import {
  Plus, FileText, CaretRight, MagnifyingGlass, Flask, Microscope, Lightning,
  ArrowRight, DownloadSimple, FileDoc, BookOpen, PawPrint, Scales, Calculator,
  ShieldCheck, Certificate, Database,
} from '@phosphor-icons/react';
import TI from '../components/brand/TI';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StatCard({ label, value, colorClass = 'text-[#111827]' }) {
  return (
    <div className="card-flat p-4">
      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] mb-1">{label}</p>
      <p className={`text-2xl font-black tabular-nums ${colorClass}`}>{value}</p>
    </div>
  );
}

function FormulationList({ formulations, loading, searchQuery, tab }) {
  const filtered = formulations.filter(f => {
    const matchSearch = f.metadata.product_name.toLowerCase().includes(searchQuery.toLowerCase());
    const mode = f.assessment_mode;
    if (tab === 'all') return matchSearch;
    if (tab === 'hazard') return matchSearch && mode === 'hazard';
    if (tab === 'tra_qra') return matchSearch && mode === 'tra_qra';
    if (tab === 'full') return matchSearch && (mode === 'full' || f.sent_to_assessment);
    if (tab === 'prop65') return matchSearch && mode === 'prop65';
    if (tab === 'sds') return matchSearch && mode === 'sds';
    if (tab === 'claims') return matchSearch && mode === 'claims';
    return matchSearch;
  });

  if (loading) return (
    <div className="p-6">
      {[1, 2, 3].map(i => (
        <div key={`sk-${i}`} className="flex items-center gap-4 mb-4">
          <div className="skeleton w-10 h-10 rounded" />
          <div className="flex-1"><div className="skeleton h-4 w-48 mb-2" /><div className="skeleton h-3 w-32" /></div>
          <div className="skeleton h-6 w-24 rounded" />
        </div>
      ))}
    </div>
  );

  if (filtered.length === 0) return (
    <div className="p-10 text-center">
      <FileText size={40} className="mx-auto text-gray-300 mb-3" />
      <p className="text-sm text-[#4B5563]">{searchQuery ? 'No matching formulations' : 'No assessments in this tab yet'}</p>
    </div>
  );

  return (
    <div className="divide-y divide-gray-200">
      {filtered.map(f => (
        <Link key={f.id} to={`/assessment/${f.id}`} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors group" data-testid={`formulation-row-${f.id}`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-[#F3F4F6] rounded flex items-center justify-center">
              <FileText size={18} className="text-[#4B5563]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[#111827] group-hover:text-[#111827] text-sm">{f.metadata.product_name}</span>
                <ModeBadgeSmall mode={f.assessment_mode || 'full'} />
                {f.version > 1 && <span className="text-[10px] font-mono text-[#4B5563] bg-[#F3F4F6] px-1.5 py-0.5 rounded">v{f.version}</span>}
              </div>
              <p className="text-xs text-[#4B5563]">
                {f.metadata.product_type && <>{f.metadata.product_type} · </>}
                <span className="tabular-nums">{new Date(f.created_at).toLocaleDateString()}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={f.overall_status} />
            {f.sent_to_assessment && <span className="text-[10px] font-semibold text-[#111827] bg-[#E5E7EB] px-1.5 py-0.5 rounded">In Assessment</span>}
            <CaretRight size={16} className="text-gray-300 group-hover:text-[#111827]" />
          </div>
        </Link>
      ))}
    </div>
  );
}

/** SDS Workspace — pick a formulation to generate a GHS-aligned SDS. */
function SDSPanel({ formulations, searchQuery }) {
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState('');

  const filtered = formulations.filter(f =>
    f.metadata.product_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSDS = async (f) => {
    setDownloadingId(f.id); setError('');
    try {
      const resp = await axios.get(`${API_URL}/api/formulations/${f.id}/sds`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `SDS_${f.metadata.product_name}.pdf`);
      document.body.appendChild(link); link.click(); link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Failed to generate SDS. Please try again.');
    } finally { setDownloadingId(null); }
  };

  if (filtered.length === 0) return (
    <div className="p-10 text-center">
      <FileDoc size={40} className="mx-auto text-gray-300 mb-3" />
      <p className="text-sm text-[#4B5563]">No formulations available. Create one to generate an SDS.</p>
    </div>
  );

  return (
    <>
      {error && <div className="p-3 text-xs text-[#9B1C1C] bg-[#FDE8E8] border-b border-[#F8B4B4]">{error}</div>}
      <div className="divide-y divide-gray-200">
        {filtered.map(f => (
          <div key={f.id} className="flex items-center justify-between p-4 hover:bg-gray-50" data-testid={`sds-row-${f.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#F3F4F6] rounded flex items-center justify-center">
                <FileDoc size={18} className="text-[#4B5563]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#111827]">{f.metadata.product_name}</p>
                <p className="text-xs text-[#4B5563]">
                  {f.ingredients?.length || 0} ingredients · GHS-aligned · 16 sections
                </p>
              </div>
            </div>
            <Button onClick={() => handleSDS(f)} disabled={downloadingId === f.id}
              variant="outline" className="rounded h-9 text-sm" data-testid={`sds-download-btn-${f.id}`}>
              <DownloadSimple size={16} className="mr-1.5" />
              {downloadingId === f.id ? 'Generating…' : 'Download SDS'}
            </Button>
          </div>
        ))}
      </div>
    </>
  );
}

export default function DashboardPage() {
  const { user, isAdmin } = useAuth(); // eslint-disable-line no-unused-vars
  const navigate = useNavigate(); // eslint-disable-line no-unused-vars
  const [formulations, setFormulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('hazard');

  const fetchFormulations = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/formulations`, { withCredentials: true });
      setFormulations(response.data);
    } catch (error) {
      console.error('Error fetching formulations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFormulations(); }, [fetchFormulations]);

  const stats = {
    total: formulations.length,
    hazard: formulations.filter(f => f.assessment_mode === 'hazard').length,
    full: formulations.filter(f => f.assessment_mode === 'full').length,
    tra_qra: formulations.filter(f => f.assessment_mode === 'tra_qra').length,
    prop65: formulations.filter(f => f.assessment_mode === 'prop65').length,
    acceptable: formulations.filter(f => f.overall_status === 'acceptable').length,
    review: formulations.filter(f => f.overall_status === 'needs_review').length,
    flagged: formulations.filter(f => f.overall_status === 'not_acceptable').length,
  };

  // 6-tab structure per the May 14 B2B restructure. Each tab is a DISTINCT
  // workflow with its own output type — no overlap. See /app/frontend/src/lib/modes.js.
  const tabs = [
    { id: 'hazard',    label: 'Screening',       icon: Lightning,    desc: 'ToxIntel Risk Screen · Scientific Risk Memo' },
    { id: 'tra_qra',   label: 'TRA / QRA',       icon: Calculator,   desc: 'Exposure-based QRA · MoS · risk characterisation' },
    { id: 'full',      label: 'Full Assessment', icon: Microscope,   desc: 'EC 1223-Style Part A + Part B · Aᴳ-signed' },
    { id: 'prop65',    label: 'Prop 65',         icon: Scales,       desc: 'NSRL/MADL · warning determination' },
    { id: 'sds',       label: 'SDS',             icon: FileDoc,      desc: 'OSHA/GHS 16-Section Safety Data Sheet' },
    { id: 'claims',    label: 'Claims Support',  icon: Certificate,  desc: 'Claims Substantiation Risk Memo' },
    { id: 'knowledge', label: 'Knowledge Base',  icon: Database,     desc: 'Aᴳ Ingredient Database · regulatory landscape' },
  ];

  const searchPlaceholder =
    activeTab === 'sds' ? 'Search formulations for SDS…'
    : 'Search formulations…';

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <SiteHeader />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#111827]"><TI color="#E63946" /> Portal</h1>
            <p className="text-sm text-[#4B5563]">Welcome, {user?.name}</p>
          </div>
          <Link to="/upload">
            <Button className="btn-primary h-10 px-5 text-sm" data-testid="new-assessment-btn">
              <Plus size={18} className="mr-1.5" weight="bold" /> New Assessment
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="Risk Screens" value={stats.hazard} />
          <StatCard label="Full Assessments" value={stats.full} />
          <StatCard label="Acceptable" value={stats.acceptable} colorClass="text-[#0E9F6E]" />
          <StatCard label="Review" value={stats.review} colorClass="text-[#D97706]" />
          <StatCard label="Flagged" value={stats.flagged} colorClass="text-[#DC2626]" />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-0 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                  isActive ? 'border-[#111827] text-[#111827]' : 'border-transparent text-[#4B5563] hover:text-[#111827]'
                }`}
                data-testid={`tab-${tab.id}`}>
                <Icon size={18} weight={isActive ? 'duotone' : 'regular'} />
                <span>{tab.label}</span>
                {isActive && (
                  <span className="hidden xl:inline text-xs text-[#4B5563] font-normal ml-1 truncate max-w-[280px]">— {tab.desc}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'knowledge' ? (
          <KnowledgeBasePanel />
        ) : (
        <div className="card-flat">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <input type="text" placeholder={searchPlaceholder} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="search-formulations-input"
                className="w-full h-9 pl-10 pr-4 rounded border border-gray-200 bg-white focus:ring-2 focus:ring-[#111827] focus:border-transparent outline-none text-sm" />
            </div>
            <div className="flex items-center gap-2 ml-4">
              <Link to="/upload">
                <Button variant="outline" className="rounded h-9 text-sm" data-testid="quick-new-btn">
                  <Plus size={16} className="mr-1.5" /> New {tabs.find(t => t.id === activeTab)?.label || 'Project'}
                </Button>
              </Link>
            </div>
          </div>

          {activeTab === 'sds' && !loading && <SDSPanel formulations={formulations} searchQuery={searchQuery} />}
          {activeTab !== 'sds' && (
            <FormulationList
              formulations={formulations} loading={loading}
              searchQuery={searchQuery} tab={activeTab}
            />
          )}
        </div>
        )}

        {/* Formulator tools (Structure Lookup + Library) — show only on screening tab */}
        {activeTab === 'hazard' && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <StructureLookupWidget />
            </div>
            <Link to="/library" className="card-flat p-5 hover:shadow-sm transition-shadow flex flex-col justify-between group" data-testid="dashboard-library-card">
              <div>
                <BookOpen size={24} className="text-[#111827] mb-2" weight="duotone" />
                <h3 className="font-semibold text-sm text-[#111827] mb-1 group-hover:underline">Private Ingredient Library</h3>
                <p className="text-xs text-[#4B5563]">
                  Build your own tagged, versioned ingredient database — scoped to your account.
                  Import from the Aᴳ library or capture from finished assessments.
                </p>
              </div>
              <ArrowRight size={16} className="text-[#4B5563] mt-3 self-end group-hover:text-[#111827]" />
            </Link>
          </div>
        )}

        {/* Assessment tools (Structure Lookup + Library link) */}
        {activeTab === 'assessment' && (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <StructureLookupWidget />
            </div>
            <Link to="/library" className="card-flat p-5 hover:shadow-sm transition-shadow flex flex-col justify-between group" data-testid="dashboard-library-card-assessment">
              <div>
                <BookOpen size={24} className="text-[#111827] mb-2" weight="duotone" />
                <h3 className="font-semibold text-sm text-[#111827] mb-1 group-hover:underline">Private Library</h3>
                <p className="text-xs text-[#4B5563]">Your own tagged, versioned ingredient DB — client-scoped.</p>
              </div>
              <ArrowRight size={16} className="text-[#4B5563] mt-3 self-end group-hover:text-[#111827]" />
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}
